/**
 * Presentation helpers for Variation Order documents and proposal history cards.
 */

import type { ApprovedVoForCompose } from "./variationOrderCurrentScope";
import type { VoDeltaLine } from "./variationOrderDiff";
import { resolveVoItemDisplayChangeReason } from "./variationOrderChangeReason";
import {
  enrichVoPlacement,
  resolveVoRoomDisplayName,
  resolveVoSystemDisplayName,
} from "./variationOrderPlacement";

export {
  filterLegacyLineChangeReasonOverrides,
  resolveVoEffectiveChangeReason,
  resolveVoItemDisplayChangeReason,
} from "./variationOrderChangeReason";

export type VoHistoryLineBrief = {
  name: string;
  quantity: number | null;
  quantityBefore?: number | null;
  quantityAfter?: number | null;
  systemName: string;
  roomName: string;
  /**
   * Explicit component-level reason override only (never the inherited VO reason).
   * Used for item-level Reason blocks on documents / history cards.
   */
  changeReason?: string | null;
};

export type ApprovedVoHistorySummary = {
  id: number;
  voNumber: string;
  approvedAt: string | null;
  publicToken: string | null;
  /** VO-level change reason (Summary section). */
  changeReason?: string | null;
  added: VoHistoryLineBrief[];
  removed: VoHistoryLineBrief[];
  quantityChanged: VoHistoryLineBrief[];
};

function formatApprovedAt(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function placementBrief(line: VoDeltaLine): Pick<VoHistoryLineBrief, "systemName" | "roomName"> {
  const enriched = enrichVoPlacement(line);
  return {
    systemName: resolveVoSystemDisplayName(enriched),
    roomName: resolveVoRoomDisplayName(enriched),
  };
}

export function buildApprovedVoHistorySummaries(
  approvedVos: ApprovedVoForCompose[],
): ApprovedVoHistorySummary[] {
  return approvedVos.map((vo) => {
    const voReason = String(vo.changeReason ?? "").trim() || null;
    const added: VoHistoryLineBrief[] = [];
    const removed: VoHistoryLineBrief[] = [];
    const quantityChanged: VoHistoryLineBrief[] = [];
    for (const line of vo.lines) {
      const place = placementBrief(line);
      const displayReason = resolveVoItemDisplayChangeReason({
        lineChangeReason: line.changeReason,
        voChangeReason: voReason,
      });
      if (line.changeType === "added") {
        added.push({
          name: line.name,
          quantity: line.quantityAfter,
          changeReason: displayReason,
          ...place,
        });
      } else if (line.changeType === "removed") {
        removed.push({
          name: line.name,
          quantity: line.quantityBefore,
          changeReason: displayReason,
          ...place,
        });
      } else if (line.changeType === "quantity_changed") {
        quantityChanged.push({
          name: line.name,
          quantity: line.quantityAfter,
          quantityBefore: line.quantityBefore,
          quantityAfter: line.quantityAfter,
          changeReason: displayReason,
          ...place,
        });
      }
    }
    return {
      id: vo.id,
      voNumber: vo.voNumber,
      approvedAt: formatApprovedAt(vo.approvedAt),
      publicToken: vo.publicToken ?? null,
      changeReason: voReason,
      added,
      removed,
      quantityChanged,
    };
  });
}

export function displayVoSystem(
  line: Pick<VoDeltaLine, "systemKey" | "systemName" | "systemId">,
): string {
  return resolveVoSystemDisplayName(line);
}

export function displayVoRoom(
  line: Pick<VoDeltaLine, "roomKey" | "roomName" | "roomId">,
): string {
  return resolveVoRoomDisplayName(line);
}

export function formatVoPlacementSuffix(systemName: string, roomName: string): string {
  return ` — ${systemName} / ${roomName}`;
}

/** Staff Link A preview path. */
export function voStaffPreviewHref(voId: number): string {
  return `/sales/vo/${voId}/preview`;
}

/** Customer public VO document path (requires publicToken). */
export function voCustomerPublicHref(publicToken: string | null | undefined): string | null {
  const t = String(publicToken ?? "").trim();
  return t ? `/vo/${t}` : null;
}

export function voViewHref(input: {
  staffLinks: boolean;
  voId: number;
  publicToken?: string | null;
}): string | null {
  if (input.staffLinks) return voStaffPreviewHref(input.voId);
  return voCustomerPublicHref(input.publicToken);
}
